import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { Layout } from '../../components/layout/Layout';
import { PageHeader } from '../../components/layout/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { useAuth } from '../../contexts/AuthContext';
import { createStudent, listClasses } from '../../modules/platform/api/schools';
import { Class } from '../../modules/platform/types';
import { showErrorToast, showSuccessToast } from '../../utils/errorHandler';
import { ROUTES } from '../../utils/constants';

interface CreateStudentFormData {
  name: string;
  class_id: string;
  roll_no?: string;
  dob?: string;
  grade: string;
  send_invite: boolean;
}

export const CreateStudent: React.FC = () => {
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [classes, setClasses] = useState<Class[]>([]);
  const navigate = useNavigate();

  const schoolId = user?.memberships[0]?.school_id;

  useEffect(() => {
    if (!schoolId) return;

    const loadClasses = async () => {
      try {
        const data = await listClasses(schoolId);
        setClasses(data);
      } catch (error) {
        showErrorToast(error);
      }
    };

    loadClasses();
  }, [schoolId]);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<CreateStudentFormData>({
    defaultValues: {
      send_invite: false,
    },
  });

  const selectedClassId = watch('class_id');
  const selectedClass = classes.find((c) => c._id === selectedClassId);

  const onSubmit = async (data: CreateStudentFormData) => {
    if (!schoolId) return;

    setIsLoading(true);
    try {
      await createStudent(schoolId, {
        ...data,
        grade: selectedClass?.grade || data.grade,
      });
      showSuccessToast('Student created successfully!');
      navigate(ROUTES.ADMIN_STUDENTS);
    } catch (error) {
      showErrorToast(error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Layout>
      <PageHeader
        title="Create Student"
        subtitle="Add a new student to your school"
      />

      <Card className="max-w-2xl">
        <CardHeader>
          <CardTitle>Student Information</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <Input
              label="Full Name"
              {...register('name', { required: 'Name is required' })}
              error={errors.name?.message}
              placeholder="Arjun Kumar"
            />

            <Select
              label="Class"
              options={[
                { value: '', label: 'Select a class' },
                ...classes.map((c) => ({
                  value: c._id,
                  label: `${c.name} (${c.grade})`,
                })),
              ]}
              {...register('class_id', { required: 'Class is required' })}
              error={errors.class_id?.message}
            />

            {selectedClass && (
              <Input
                label="Grade"
                value={selectedClass.grade}
                disabled
                helperText="Automatically set from selected class"
              />
            )}

            <Input
              label="Roll Number"
              {...register('roll_no')}
              error={errors.roll_no?.message}
              placeholder="2025-001"
            />

            <Input
              label="Date of Birth"
              type="date"
              {...register('dob')}
              error={errors.dob?.message}
            />

            <div className="flex gap-4 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(ROUTES.ADMIN_STUDENTS)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button type="submit" isLoading={isLoading} className="flex-1">
                Create Student
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </Layout>
  );
};

